import { useMemo, useState } from "react";
import { Archive, ArrowDown, ArrowUp, ChevronsUpDown, ChevronDown, ChevronRight } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const COLS = [["project_name", "Name"], ["type", "Type"], ["status", "Status"], ["grand_total", "Total Price"]];
const statusOf = (r) => (r.is_archived ? "archived" : r.status);

const Header = ({ sort, setSort }) => (
  <tr>
    {COLS.map(([k, label]) => {
      const on = sort.key === k;
      const Icon = !on ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
      return (
        <th key={k} className={`px-3 py-2 ${k === "grand_total" ? "text-right" : "text-left"}`}>
          <button type="button" onClick={() => setSort({ key: k, dir: on && sort.dir === "asc" ? "desc" : "asc" })}
                  className={`inline-flex items-center gap-1 hover:text-foreground ${on ? "text-foreground" : ""}`}
                  data-testid={`open-budgets-sort-${k}`}>
            {label}<Icon className={`w-3 h-3 ${on ? "" : "opacity-40"}`} />
          </button>
        </th>
      );
    })}
  </tr>
);

const Row = ({ r, onOpen }) => (
  <tr onClick={() => onOpen(r)} className={`border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 ${r.is_archived ? "opacity-50" : ""}`}
      data-testid={`open-budgets-row-${r.budget_id}`}>
    <td className="px-3 py-2 font-medium">{r.project_name}</td>
    <td className="px-3">{r.type}</td>
    <td className="px-3 capitalize">{statusOf(r)}</td>
    <td className="px-3 text-right tabular-nums">{money(r.grand_total)}</td>
  </tr>
);

/** BB-DASH-1 — one OPEN BUDGETS table. Subtotals count open (not archived) budgets only. */
export const OpenBudgetsTable = ({ rows, onOpen, openArchive = false }) => {
  const [sort, setSort] = useState({ key: "project_name", dir: "asc" });
  const [archToggled, setArchToggled] = useState(null);
  const archOpen = archToggled ?? openArchive;
  const live = rows.filter((r) => !r.is_archived);
  const archived = rows.filter((r) => r.is_archived);
  const grouped = false;
  const sorted = useMemo(() => {
    const val = (r) => (sort.key === "status" ? statusOf(r) : r[sort.key]);
    const out = rows.filter((r) => !r.is_archived).sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === "number" ? x - y : String(x || "").localeCompare(String(y || ""));
      return sort.dir === "asc" ? c : -c;
    });
    return out;
  }, [rows, sort]);
  const groups = useMemo(() => {
    if (!grouped) return [{ type: null, rows: sorted }];
    const by = {};
    sorted.forEach((r) => { (by[r.type] = by[r.type] || []).push(r); });
    return Object.keys(by).sort().map((type) => ({ type, rows: by[type] }));
  }, [sorted, grouped]);
  const openSum = (rs) => rs.filter((r) => !r.is_archived).reduce((s, r) => s + r.grand_total, 0);

  return (
    <div>
      <table className="w-full text-sm" data-testid="open-budgets-table">
        <thead className="bg-muted/40 text-[11px] font-semibold text-muted-foreground"><Header sort={sort} setSort={setSort} /></thead>
        {groups.map((g) => (
          <tbody key={g.type || "all"} data-testid={`open-budgets-group-${g.type || "all"}`}>
            {g.type && (
              <tr className="bg-slate-50 dark:bg-slate-800/50 text-xs">
                <td className="px-3 py-1.5 font-semibold" colSpan={3}>{g.type}
                  <span className="ml-2 font-normal normal-case text-slate-500">{g.rows.filter((r) => !r.is_archived).length} open</span></td>
                <td className="px-3 text-right font-semibold tabular-nums" data-testid={`open-budgets-subtotal-${g.type}`}>{money(openSum(g.rows))}</td>
              </tr>
            )}
            {g.rows.map((r) => <Row key={r.budget_id} r={r} onOpen={onOpen} />)}
          </tbody>
        ))}
      </table>
      {live.length === 0 && <div className="p-4 text-sm text-slate-500">Nothing open.</div>}
      {archived.length > 0 && (
        <div className="border-t border-border" data-testid="open-budgets-archive">
          <button type="button" onClick={() => setArchToggled(!archOpen)} data-testid="open-budgets-archive-toggle"
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
            {archOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Archive className="h-3.5 w-3.5" /> Archive <span className="font-normal" data-testid="open-budgets-archive-count">· {archived.length}</span>
          </button>
          {archOpen && (
            <table className="w-full text-sm" data-testid="open-budgets-archive-table">
              <tbody>{archived.map((r) => <Row key={r.budget_id} r={r} onOpen={onOpen} />)}</tbody>
            </table>)}
        </div>)}
    </div>
  );
};
